<template>
  <div>
    <q-select
      ref="addPeople"
      v-model="value"
      transition-duration="100"
      :placeholder="value.length ? '' : placeholder"
      outlined
      use-input
      use-chips
      multiple
      hide-dropdown-icon
      input-debounce="0"
      new-value-mode="add-unique"
      dense
      :options="invitePeopleOptions"
      @filter="invitePeopleFilter"
      @new-value="createValue"
      @input-value="inputValueChange"
      @blur="onBlur"
    >
      <template #selected-item="scope">
        <q-chip
          removable
          dense
          :tabindex="scope.tabindex"
          :color="getChipColor(scope.opt)"
          @remove="scope.removeAtIndex(scope.index)"
        >
          <template v-if="scope.opt.type === 'user'">
            <q-avatar round :class="getChipAvatarColor(scope.opt.email)">
              <q-img
                v-if="scope.opt?.picture"
                :src="scope.opt.picture"
                referrerpolicy="no-referrer"
                no-spinner
              />
              <q-icon v-else name="person" />
            </q-avatar>
            <div v-if="scope.opt.family_name && scope.opt.given_name" class="q-pl-xs">
              {{ scope.opt.given_name }} {{ scope.opt.family_name }}
            </div>
            <div v-else class="q-pl-xs">
              {{ scope.opt.email }}
            </div>
          </template>
          <template v-else-if="scope.opt.type === 'group'">
            <q-avatar round>
              <q-icon name="group" />
            </q-avatar>
            <div class="q-pl-xs">
              {{ scope.opt.name }}
            </div>
          </template>
        </q-chip>
      </template>
      <template #option="scope">
        <q-list>
          <q-separator />
          <q-item v-ripple v-close-popup v-bind="scope.itemProps" clickable bordered>
            <q-item-section>
              <template v-if="scope.opt.type === 'user'">
                <div class="flex items-center">
                  <q-avatar size="32px" round class="q-mr-sm bg-grey-4">
                    <q-img
                      v-if="scope.opt?.picture"
                      :src="scope.opt.picture"
                      referrerpolicy="no-referrer"
                      no-spinner
                    />
                    <q-icon v-else name="person" />
                  </q-avatar>
                  <div>
                    <div class="text-weight-medium">{{ userToName(scope.opt) }}</div>
                    <div style="font-size: 12px">
                      {{ scope.opt.email }}
                    </div>
                  </div>
                </div>
              </template>
              <template v-else-if="scope.opt.type === 'group'">
                <div class="flex items-center">
                  <q-avatar size="32px" round class="q-mr-sm bg-grey-4">
                    <q-icon name="group" />
                  </q-avatar>
                  <div>
                    <div class="text-weight-medium">{{ scope.opt.name }}</div>
                  </div>
                </div>
              </template>
            </q-item-section>
          </q-item>
        </q-list>
      </template>
    </q-select>
    <div
      v-if="hasPersonalEmailDomains"
      class="q-pa-sm q-mt-md"
      :class="$q.dark.isActive ? 'text-grey-3 bg-grey-8' : 'text-grey-8 bg-grey-3'"
    >
      <q-icon name="warning" class="q-mr-sm" />
      We noticed your email list contains personal email account(s), such as gmail, yahoo, hotmail,
      etc. You may proceed, but personal email accounts are not supported for account creation.
      Please use work emails instead.
    </div>
  </div>
</template>

<script lang="ts">
import { type AccessUser, type GroupAttributes, isValidEmail } from 'common'
import { useUserStore } from '@/stores/user'
import { useStripeStore } from '@/stores/stripe'
import { QSelect } from 'quasar'
import type { PropType } from 'vue'
import { canExternallyShare, isInternalEmail } from '@/utils.ts'
import useGroupStore from '@/stores/group.ts'

const personalEmailDomains = [
  '@gmail.com',
  '@yahoo.com',
  '@hotmail.com',
  '@outlook.com',
  '@icloud.com',
  '@me.com',
  '@mac.com',
  '@aol.com',
  '@protonmail.com',
  '@yandex.com',
  '@mail.com',
  '@inbox.com',
  '@zoho.com'
]

export type InvitePeopleOption =
  | (AccessUser & { type: 'user' })
  | (GroupAttributes & { type: 'group' })

export default {
  name: 'EmailDropdownSelect',
  props: {
    modelValue: {
      type: Array<InvitePeopleOption>,
      required: true
    },
    peopleWithAccess: {
      type: Array<AccessUser>,
      default: []
    },
    reportIsInPortfolio: {
      type: Boolean as PropType<boolean | null>,
      default: null
    },
    allowAddByGroup: {
      type: Boolean as PropType<boolean | null>,
      default: false,
      required: false
    },
    placeholder: {
      type: String as PropType<string>,
      default: 'Add people to your property report',
      required: false
    },
    disableCreateNew: {
      type: Boolean as PropType<boolean | null>,
      default: false,
      required: false
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      invitePeopleOptions: [] as InvitePeopleOption[],
      currentEditValue: '',
      isValidEmail
    }
  },
  computed: {
    value: {
      get() {
        return this.modelValue
      },
      set(value: InvitePeopleOption[]) {
        this.$emit('update:modelValue', value)
      }
    },
    hasPersonalEmailDomains() {
      // Check if any of the emails in the value array contain personal email domains
      return this.value.some((option) => {
        if (option.type === 'user') {
          return personalEmailDomains.some((domain) => option.email.toLowerCase().endsWith(domain))
        }
        return false
      })
    },
    isAdmin() {
      return useUserStore().isAdmin
    },
    organization() {
      return useUserStore().organization
    },
    peopleInOrgOrShared(): InvitePeopleOption[] {
      return useUserStore().peopleInOrgOrShared.map((_) => {
        return {
          ..._,
          type: 'user' as const
        }
      })
    },
    billedPerSeat() {
      return useStripeStore().billedPerSeat
    },
    billedPerUnit() {
      return useStripeStore().billedPerUnit
    },
    organizationMembers() {
      return useUserStore().organizationMembers
    },
    groups(): InvitePeopleOption[] {
      return useGroupStore().groups.map((_) => {
        return {
          ..._,
          type: 'group' as const
        }
      })
    }
  },
  watch: {
    value() {
      if (this.value.length) {
        const addPeopleEl = this.$refs.addPeople as QSelect
        addPeopleEl.updateInputValue('')
      }
    }
  },
  methods: {
    getChipColor(option: InvitePeopleOption) {
      if (option.type === 'user') {
        if (!this.emailCanBeExternallyShared(option.email)) {
          if (this.$q.dark.isActive) {
            return 'primary-light'
          }
          return 'primary-extra-light'
        }
      }
      if (this.$q.dark.isActive) {
        return 'grey-7'
      }
      return 'grey-3'
    },
    getChipAvatarColor(email: string) {
      if (!this.emailCanBeExternallyShared(email)) {
        return 'bg-primary'
      }
      if (this.$q.dark.isActive) {
        return 'bg-grey-8'
      }
      return 'bg-grey-4'
    },
    emailCanBeExternallyShared(email: string) {
      if (
        canExternallyShare(
          this.billedPerUnit,
          this.billedPerSeat,
          this.$auth0.user.value?.internalFeatureFlags,
          this.reportIsInPortfolio,
          this.isAdmin
        )
      ) {
        return true
      }

      return isInternalEmail(email, this.organizationMembers)
    },
    invitePeopleFilter(val: string, update: (callbackFn: () => void) => void) {
      const peopleWithAccess = this.peopleWithAccess.concat(this.value).map((_) => _.id)
      let peopleInOrgOrShared = this.peopleInOrgOrShared.filter(
        (_) => !peopleWithAccess.includes(_.id)
      )

      if (
        !canExternallyShare(
          this.billedPerUnit,
          this.billedPerSeat,
          this.$auth0.user.value?.internalFeatureFlags,
          this.reportIsInPortfolio,
          this.isAdmin
        )
      ) {
        const orgMemberIds = this.organizationMembers.map((_) => _.id)
        peopleInOrgOrShared = peopleInOrgOrShared.filter((_) => orgMemberIds.includes(_.id))
      }

      let availableGroups: InvitePeopleOption[] = []
      if (this.allowAddByGroup) {
        availableGroups = this.groups
      }

      if (val === '') {
        update(() => {
          const combinedOptions = [...peopleInOrgOrShared, ...availableGroups]
          this.invitePeopleOptions = combinedOptions.slice(0, 5)
        })
        return
      }
      update(() => {
        const search = val.toLowerCase().split(' ').join('')
        const userOptions = peopleInOrgOrShared.filter((_) => {
          const givenName = (_.given_name || '').toLowerCase().split(' ').join('')
          const familyName = (_.family_name || '').toLowerCase().split(' ').join('')
          return (
            givenName.includes(search) ||
            familyName.includes(search) ||
            (givenName + familyName).includes(search) ||
            _.email.toLowerCase().includes(search)
          )
        })

        const groupOptions = availableGroups.filter((_) => {
          return _.name.toLowerCase().split(' ').join('').includes(search)
        })

        const combinedOptions = [...userOptions, ...groupOptions]

        if (
          combinedOptions.length === 0 &&
          this.isValidEmail(val) &&
          this.emailCanBeExternallyShared(val.trim())
        ) {
          this.invitePeopleOptions = [
            {
              id: null,
              given_name: null,
              family_name: null,
              picture: null,
              email: val.trim(),
              type: 'user'
            }
          ]
        } else {
          this.invitePeopleOptions = combinedOptions.slice(0, 5)
        }
      })
    },
    createValue(val: string, done: (val: InvitePeopleOption) => void) {
      if (this.disableCreateNew) {
        return
      }
      if (
        this.peopleWithAccess.find(
          (_) => _.email.toLocaleLowerCase() === val.toLocaleLowerCase()
        ) ||
        this.modelValue.find((_) => _.email.toLocaleLowerCase() === val.toLocaleLowerCase())
      ) {
        this.$q.notify({
          message: 'Removed duplicate emails',
          color: 'positive',
          position: 'top'
        })
        return
      }

      if (this.isValidEmail(val)) {
        const override = this.peopleInOrgOrShared.find((_) => _.email.toLocaleLowerCase() === val)
        if (override) {
          done(override)
        } else {
          done({
            id: null,
            given_name: null,
            family_name: null,
            picture: null,
            email: val.trim(),
            type: 'user'
          })
        }
      }
    },
    userToName(user: AccessUser): string {
      const suffix = user.id === this.$auth0.user.value?.sub ? ' (you)' : ''
      if (user.given_name && user.family_name) {
        return `${user.given_name} ${user.family_name}${suffix}`
      }
      return `${user.email}${suffix}`
    },
    inputValueChange(value: string) {
      this.currentEditValue = value
    },
    onBlur() {
      if (this.currentEditValue) {
        const value = this.currentEditValue.split(',').join(' ').split(';').join(' ')
        if (
          value
            .split(' ')
            .map(this.isValidEmail)
            .filter((_) => !_).length === 0
        ) {
          const emails = new Set(
            value
              .split(' ')
              .map((_) => _.trim().toLowerCase())
              .filter((_) => _)
          )
          emails.forEach((email) => {
            this.createValue(email, (val) => this.value.push(val))
          })
        }
        this.currentEditValue = ''
      }
    }
  }
}
</script>
